import { db } from "./dbClient";

// ─── RegulatoryCorrespondencePage.jsx ─────────────────────────────────────────
// Track examiner requests, subpoenas, 314(a), informal inquiries, and other
// regulatory correspondence. Full CRUD, audit log, CSV export.

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";

// ─── Constants ────────────────────────────────────────────────────────────────
const TYPES = [
  { value: "exam_request",    label: "Exam Request" },
  { value: "subpoena",        label: "Subpoena" },
  { value: "314a_request",    label: "314(a) Request" },
  { value: "informal_inquiry",label: "Informal Inquiry" },
  { value: "formal_order",    label: "Formal Order" },
  { value: "examination",     label: "Examination" },
  { value: "other",           label: "Other" },
];

const STATUSES = [
  { value: "open",       label: "Open",       color: "#FF8C00" },
  { value: "in_progress",label: "In Progress",color: "#00D4FF" },
  { value: "responded",  label: "Responded",  color: "#00E676" },
  { value: "closed",     label: "Closed",     color: "#4A5870" },
];

function getTranslatedCorrespondenceType(type, t) {
  const typeMap = {
    "exam_request": "correspondence.types.exam_request",
    "subpoena": "correspondence.types.subpoena",
    "314a_request": "correspondence.types.314a_request",
    "informal_inquiry": "correspondence.types.informal_inquiry",
    "formal_order": "correspondence.types.formal_order",
    "examination": "correspondence.types.examination",
    "other": "correspondence.types.other",
  };
  return t(typeMap[type] || type);
}

function getTranslatedCorrespondenceStatus(status, t) {
  const statusMap = {
    "open": "correspondence.statuses.open",
    "in_progress": "correspondence.statuses.in_progress",
    "responded": "correspondence.statuses.responded",
    "closed": "correspondence.statuses.closed",
  };
  return t(statusMap[status] || status);
}

function statusMeta(s) {
  return STATUSES.find(x => x.value === s) || { label: s, color: "#8A9BB5" };
}
function typeLabel(t) {
  return TYPES.find(x => x.value === t)?.label || t;
}
function today() { return new Date().toISOString().slice(0, 10); }
function isOverdue(rec) {
  if (!rec.due_date) return false;
  if (rec.status === "closed" || rec.status === "responded") return false;
  return rec.due_date < today();
}

function downloadCSV(rows) {
  const headers = ["ID","Type","Correspondent","Subject","Received","Due Date","Status","Assigned To","Response Notes"];
  const lines = [headers.join(",")];
  rows.forEach(r => {
    lines.push([
      r.id,
      `"${typeLabel(r.request_type)}"`,
      `"${(r.correspondent || "").replace(/"/g,"'")}"`,
      `"${(r.subject || "").replace(/"/g,"'")}"`,
      r.received_date || "",
      r.due_date || "",
      r.status || "",
      `"${(r.assigned_to || "").replace(/"/g,"'")}"`,
      `"${(r.response_notes || "").replace(/"/g,"'")}"`,
    ].join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
  a.download = `regulatory-correspondence-${today()}.csv`; a.click();
}

const emptyForm = () => ({
  request_type: "exam_request",
  correspondent: "",
  subject: "",
  received_date: today(),
  due_date: "",
  status: "open",
  assigned_to: "",
  response_notes: "",
});

// ─── Sub-components ───────────────────────────────────────────────────────────
function Badge({ status }) {
  const { t } = useTranslation();
  const { label, color } = statusMeta(status);
  return (
    <span style={{ background: `${color}22`, color, borderRadius: 5, fontSize: 10, fontWeight: 700, padding: "3px 8px", letterSpacing: "0.06em" }}>
      {getTranslatedCorrespondenceStatus(status, t).toUpperCase()}
    </span>
  );
}

function Field({ label, children, span2 }) {
  const C = useTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: span2 ? "span 2" : "span 1" }}>
      <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em" }}>{label.toUpperCase()}</label>
      {children}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function RegulatoryCorrespondencePage({ company, currentUser, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);

  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null); // record being edited
  const [form, setForm]             = useState(emptyForm());
  const [saving, setSaving]         = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo]     = useState("");

  const inputStyle = (C) => ({
    background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.text, fontSize: 13, padding: "8px 12px",
    fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box",
  });
  const selStyle = (C) => ({ ...inputStyle(C), cursor: "pointer" });

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { data, error: err } = await db
        .from("regulatory_correspondence")
        .select("*")
        .eq("company_id", company.id)
        .order("received_date", false);
      if (err) {
        setError(err.message);
        setRecords([]);
      } else {
        setRecords((data || []).sort((a, b) => (new Date(b.received_date) - new Date(a.received_date))));
      }
    } catch (e) {
      setError(e.message);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // ─── Filtered list ─────────────────────────────────────────────────────────
  const filtered = records.filter(r => {
    if (filterType !== "all" && r.request_type !== filterType) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (filterFrom && r.received_date && r.received_date < filterFrom) return false;
    if (filterTo && r.received_date && r.received_date > filterTo) return false;
    return true;
  });

  // ─── Modal helpers ─────────────────────────────────────────────────────────
  function openAdd() { setEditing(null); setForm(emptyForm()); setShowModal(true); }
  function openEdit(r) { setEditing(r); setForm({ ...r }); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditing(null); setForm(emptyForm()); }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.request_type || !form.correspondent || !form.subject) return;
    setSaving(true);
    try {
      // Create clean payload with only valid columns
      const payload = {
        company_id: company.id,
        request_type: String(form.request_type || ''),
        correspondent: String(form.correspondent || ''),
        subject: String(form.subject || ''),
        received_date: form.received_date || null,
        due_date: form.due_date || null,
        status: String(form.status || 'open'),
        assigned_to: String(form.assigned_to || ''),
        response_notes: String(form.response_notes || ''),
      };

      console.log('[Correspondence] Saving with payload:', payload);

      if (editing) {
        const { error: err } = await db.from("regulatory_correspondence").update(payload).eq("id", editing.id);
        if (err) { setError(err.message); console.error('[Correspondence] Update error:', err); return; }
        await log({ action: "correspondence_updated", module: "regulatory_correspondence", entityId: editing.id, entityName: form.subject, oldValue: editing, newValue: payload });
      } else {
        // Insert single record - don't wrap in array
        const { data, error: err } = await db.from("regulatory_correspondence").insert(payload);
        if (err) { setError(err.message); console.error('[Correspondence] Insert error:', err); return; }
        await log({ action: "correspondence_created", module: "regulatory_correspondence", entityId: data?.id, entityName: form.subject, newValue: payload });
      }

      closeModal();
      fetchRecords();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleClose(rec) {
    try {
      const respondedAt = new Date().toISOString();
      const { error: err } = await db
        .from("regulatory_correspondence")
        .update({ status: "closed", responded_at: respondedAt })
        .eq("id", rec.id);
      if (err) { setError(err.message); return; }
      await log({ action: "correspondence_closed", module: "regulatory_correspondence", entityId: rec.id, entityName: rec.subject, newValue: { status: "closed", responded_at: respondedAt } });
      fetchRecords();
    } catch (e) {
      setError(e.message);
    }
  }

  // ─── Derived stats ─────────────────────────────────────────────────────────
  const totalOpen   = records.filter(r => r.status === "open" || r.status === "in_progress").length;
  const totalOverdue = records.filter(isOverdue).length;
  const totalClosed  = records.filter(r => r.status === "closed").length;

  const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "24px 28px", fontFamily: "'DM Mono', monospace", color: C.text, background: C.bg, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontFamily: "'Syne', sans-serif", fontWeight: 700 }}>{t('correspondence.title')}</h1>
          <p style={{ margin: "4px 0 0", color: C.textMid, fontSize: 12 }}>{t('correspondence.subtitle')}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => downloadCSV(filtered)} style={{ background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, padding: "9px 16px", fontSize: 12, cursor: "pointer" }}>
            EXPORT CSV
          </button>
          <button onClick={openAdd} style={{ background: C.accent, color: "#000", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            + NEW
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 22 }}>
        {[
          { label: "Open / In Progress", value: totalOpen,   color: C.warn },
          { label: "Overdue Response",   value: totalOverdue, color: C.danger },
          { label: "Closed",             value: totalClosed,  color: C.success },
        ].map(k => (
          <div key={k.label} style={{ ...card, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 10, color: C.textMid, letterSpacing: "0.1em" }}>{k.label.toUpperCase()}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ ...card, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 18 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 10, color: C.textMid, letterSpacing: "0.08em" }}>TYPE</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...selStyle(C), width: 180 }}>
            <option value="all">{t('correspondence.allTypes')}</option>
            {TYPES.map(ct => <option key={ct.value} value={ct.value}>{getTranslatedCorrespondenceType(ct.value, t)}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 10, color: C.textMid, letterSpacing: "0.08em" }}>STATUS</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selStyle(C), width: 160 }}>
            <option value="all">{t('correspondence.allStatuses')}</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{getTranslatedCorrespondenceStatus(s.value, t)}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 10, color: C.textMid, letterSpacing: "0.08em" }}>RECEIVED FROM</label>
          <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={{ ...inputStyle(C), width: 150 }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 10, color: C.textMid, letterSpacing: "0.08em" }}>RECEIVED TO</label>
          <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} style={{ ...inputStyle(C), width: 150 }} />
        </div>
        {(filterType !== "all" || filterStatus !== "all" || filterFrom || filterTo) && (
          <button onClick={() => { setFilterType("all"); setFilterStatus("all"); setFilterFrom(""); setFilterTo(""); }}
            style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, padding: "8px 14px", cursor: "pointer", fontSize: 11, alignSelf: "flex-end" }}>
            CLEAR
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, borderRadius: 8, padding: "10px 16px", color: C.danger, fontSize: 12, marginBottom: 14 }}>{error}</div>
      )}

      {/* Table */}
      <div style={card}>
        {loading ? (
          <div style={{ color: C.textMid, textAlign: "center", padding: 40 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: C.textDim, textAlign: "center", padding: 50, fontSize: 13 }}>No correspondence records found.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Type","Counterparty","Subject","Received","Response Due","Status","Assigned","Actions"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: C.textMid, fontSize: 10, letterSpacing: "0.08em", fontWeight: 600 }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const overdue = isOverdue(r);
                  return (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}30`, background: overdue ? `${C.danger}09` : "transparent", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = overdue ? `${C.danger}14` : `${C.accent}08`}
                      onMouseLeave={e => e.currentTarget.style.background = overdue ? `${C.danger}09` : "transparent"}>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: `${C.accent}18`, color: C.accent, borderRadius: 4, fontSize: 10, padding: "2px 7px" }}>{getTranslatedCorrespondenceType(r.type, t)}</span>
                      </td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{r.counterparty}</td>
                      <td style={{ padding: "10px 12px", color: C.text, maxWidth: 220 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.subject}</div>
                      </td>
                      <td style={{ padding: "10px 12px", color: C.textMid }}>{r.received_date ? new Date(r.received_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      <td style={{ padding: "10px 12px", color: overdue ? C.danger : C.textMid, fontWeight: overdue ? 700 : 400 }}>
                        {r.due_date ? new Date(r.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"} {overdue && "⚠"}
                      </td>
                      <td style={{ padding: "10px 12px" }}><Badge status={r.status} /></td>
                      <td style={{ padding: "10px 12px", color: C.textMid }}>{r.assigned_to || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => openEdit(r)} style={{ background: `${C.accent}20`, border: "none", borderRadius: 5, color: C.accent, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Edit</button>
                          {r.status !== "closed" && (
                            <button onClick={() => handleClose(r)} style={{ background: `${C.success}18`, border: "none", borderRadius: 5, color: C.success, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Close</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && closeModal()}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: 640, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontFamily: "'Syne', sans-serif", fontSize: 18 }}>{editing ? "Edit Correspondence" : "New Correspondence"}</h2>
              <button onClick={closeModal} style={{ background: "transparent", border: "none", color: C.textMid, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="Type">
                <select value={form.request_type} onChange={e => set("request_type", e.target.value)} style={selStyle(C)}>
                  {TYPES.map(ct => <option key={ct.value} value={ct.value}>{getTranslatedCorrespondenceType(ct.value, t)}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => set("status", e.target.value)} style={selStyle(C)}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{getTranslatedCorrespondenceStatus(s.value, t)}</option>)}
                </select>
              </Field>
              <Field label="Counterparty / Regulator" span2>
                <input value={form.correspondent} onChange={e => set("correspondent", e.target.value)} placeholder="e.g. FinCEN, OCC, DOJ..." style={inputStyle(C)} />
              </Field>
              <Field label="Subject / Matter" span2>
                <input value={form.subject} onChange={e => set("subject", e.target.value)} placeholder="Brief description of the matter" style={inputStyle(C)} />
              </Field>
              <Field label="Received Date">
                <input type="date" value={form.received_date} onChange={e => set("received_date", e.target.value)} style={inputStyle(C)} />
              </Field>
              <Field label="Response Due Date">
                <input type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} style={inputStyle(C)} />
              </Field>
              <Field label="Assigned To">
                <select value={form.assigned_to} onChange={e => set("assigned_to", e.target.value)} style={selStyle(C)}>
                  <option value="">— Unassigned —</option>
                  {(team || []).map(m => (
                    <option key={m.id} value={`${m.firstName || ""} ${m.lastName || ""}`.trim()}>
                      {`${m.firstName || ""} ${m.lastName || ""}`.trim()} {m.role ? `(${m.role})` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Response Notes" span2>
                <textarea value={form.response_notes} onChange={e => set("response_notes", e.target.value)} rows={3} style={{ ...inputStyle(C), resize: "vertical" }} placeholder="Details of our response..." />
              </Field>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
              <button onClick={closeModal} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, padding: "10px 20px", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.correspondent || !form.subject}
                style={{ background: saving ? C.textDim : C.accent, color: "#000", border: "none", borderRadius: 8, padding: "10px 24px", fontWeight: 700, fontSize: 13, cursor: saving ? "not-allowed" : "pointer" }}>
                {saving ? "Saving..." : editing ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
